import { useState } from "react";
import { CheckCircle2, Clock, Wallet, Eye, Lock, Unlock, CreditCard, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { usePoll } from "@/hooks/usePoll";
import { api, errMsg, rp, fmtTime } from "@/lib/api";
import { Panel, Empty, StageBadge, StatCard } from "@/components/common";
import BillDetailDialog from "@/components/BillDetailDialog";
import PaymentDialog from "@/components/PaymentDialog";

export default function Kasir() {
  const stats = usePoll("/stats/kasir", 5000);
  const bills = usePoll("/bills", 5000, { status: "active" });
  const [detail, setDetail] = useState(null);
  const [paying, setPaying] = useState(null);
  const s = stats.data || { completed: 0, pending: 0, total_amount: 0, ready_to_pay: 0 };
  const rows = bills.data || [];

  const reloadAll = () => { stats.reload(); bills.reload(); };
  const toggleClose = async (b) => {
    try {
      await api.post(`/bills/${b.id}/close`);
      toast.success(b.order_closed ? "Order dibuka kembali" : "Order ditandai selesai");
      bills.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard testId="kasir-stat-completed" label="Order Selesai (hari ini)" value={s.completed} icon={CheckCircle2} accent="emerald" />
        <StatCard testId="kasir-stat-pending" label="Order Pending" value={s.pending} icon={Clock} accent="amber" hint={`${s.ready_to_pay} siap dibayar`} />
        <StatCard testId="kasir-stat-total" label="Total Transaksi (hari ini)" value={rp(s.total_amount)} icon={Wallet} />
      </div>
      <Panel className="mt-6 overflow-x-auto">
        <div className="flex items-center justify-between border-b border-stone-100 px-5 py-4">
          <h3 className="text-lg font-semibold">Order Pelanggan</h3>
          <span className="text-xs text-zinc-500">Tombol Bayar aktif setelah Checker klik Selesai</span>
        </div>
        {rows.length === 0 ? <div className="p-5"><Empty icon={Inbox} text="Belum ada order berjalan" testId="kasir-empty" /></div> : (
          <Table data-testid="kasir-orders-table">
            <TableHeader><TableRow><TableHead>No. Bill</TableHead><TableHead>Pelanggan</TableHead><TableHead>Meja</TableHead><TableHead>Item</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((b) => {
                const ready = b.stage === "siap_bayar";
                return (
                  <TableRow key={b.id} data-testid={`kasir-row-${b.code}`} className={ready ? "bg-emerald-50/40" : "hover:bg-orange-50/50"}>
                    <TableCell><p className="font-mono font-semibold">{b.code}</p><p className="text-xs text-zinc-500">{fmtTime(b.created_at)}</p></TableCell>
                    <TableCell className="font-medium">{b.customer_name}</TableCell>
                    <TableCell>{b.table_name}</TableCell>
                    <TableCell>{b.items.reduce((n, i) => n + i.qty, 0)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        <StageBadge stage={b.stage} testId={`kasir-stage-${b.code}`} />
                        {b.order_closed && <span className="text-[11px] font-semibold text-zinc-500">Order ditutup</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">{rp(b.totals.total)}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" variant="outline" data-testid={`kasir-detail-${b.code}`} onClick={() => setDetail(b)}><Eye className="mr-1 h-4 w-4" />Rincian</Button>
                        <Button size="sm" variant="outline" data-testid={`kasir-close-${b.code}`} disabled={!b.items.length} onClick={() => toggleClose(b)}>
                          {b.order_closed ? <Unlock className="mr-1 h-4 w-4" /> : <Lock className="mr-1 h-4 w-4" />}{b.order_closed ? "Buka Order" : "Order Selesai"}
                        </Button>
                        <Button size="sm" data-testid={`kasir-pay-${b.code}`} disabled={!ready} onClick={() => setPaying(b)} className="bg-orange-600 hover:bg-orange-700 active:scale-95 transition-transform">
                          <CreditCard className="mr-1 h-4 w-4" />Bayar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Panel>
      <BillDetailDialog bill={detail} open={!!detail} onOpenChange={(o) => !o && setDetail(null)} />
      <PaymentDialog bill={paying} open={!!paying} onOpenChange={(o) => { if (!o) { setPaying(null); reloadAll(); } }} onPaid={reloadAll} />
    </div>
  );
}
